import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { F1 } from '@client/new/helpers/types';
import {
  COMMUNITY_MENTION,
  splitTextByChannelMention,
  getChannelMentionTriggerPosition,
} from '../utils/channelMention';
import { findSegmentAt, parseEditable } from '../utils/mdEditable';
import {
  renderedOffsetToMarkdownOffset,
  markdownOffsetToRenderedOffset,
} from '../utils/mdEditable';
import { MDMentionMenu } from './MDMentionMenu';
import { MDLinkTooltip } from './MDLinkTooltip';
import { MDLinkModal } from './MDLinkModal';
import * as styles from './MDTextarea.css';
import { insertLinkAtSelection } from '../view-model/useMDTextareaLink.viewModel';

type MDTextareaProps = { value: string; onChange: F1<string> };
type StyledProps = { placeholder?: string };
type Props = MDTextareaProps & StyledProps;
export type MDTextareaHandle = {
  openAddLink: () => void;
};
const sanitizeUrl = (url: string): string => {
  const trimmed = url.trim();
  const allowed = ['http:', 'https:', 'mailto:', 'tel:'];
  const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed);
  if (hasScheme && !allowed.some((s) => trimmed.startsWith(s))) {
    return '';
  }
  if (trimmed.startsWith('javascript:') || trimmed.startsWith('data:')) {
    return '';
  }
  return trimmed;
};
export const getCaretOffset = (container: HTMLElement): number | null => {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return null;
  const range = selection.getRangeAt(0);
  if (!container.contains(range.startContainer)) return null;

  const preRange = range.cloneRange();
  preRange.selectNodeContents(container);
  preRange.setEnd(range.startContainer, range.startOffset);
  return preRange.toString().length;
};

const getSelectionOffsets = (
  container: HTMLElement,
): {
  start: number;
  end: number;
  text: string;
  isSingleTextNode: boolean;
} | null => {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return null;
  const range = selection.getRangeAt(0);
  if (
    !container.contains(range.startContainer) ||
    !container.contains(range.endContainer)
  )
    return null;

  const startRange = range.cloneRange();
  startRange.selectNodeContents(container);
  startRange.setEnd(range.startContainer, range.startOffset);
  const start = startRange.toString().length;

  const endRange = range.cloneRange();
  endRange.selectNodeContents(container);
  endRange.setEnd(range.endContainer, range.endOffset);
  const end = endRange.toString().length;

  const text = range.toString();
  const isSingleTextNode =
    range.startContainer === range.endContainer &&
    range.startContainer.nodeType === Node.TEXT_NODE &&
    !(
      range.startContainer.parentElement?.closest('a') ||
      range.startContainer.parentElement?.dataset.mention === 'true'
    );

  return { start, end, text, isSingleTextNode };
};

const setCaretOffset = (container: HTMLElement, offset: number) => {
  const selection = window.getSelection();
  if (!selection) return;

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null,
  );

  let remaining = offset;
  let currentNode = walker.nextNode() as Text | null;

  while (currentNode) {
    const length = currentNode.textContent?.length ?? 0;
    if (remaining <= length) {
      if (
        remaining === length &&
        currentNode.parentElement &&
        (currentNode.parentElement.tagName === 'A' ||
          currentNode.parentElement.dataset.mention === 'true')
      ) {
        const range = document.createRange();
        const nextTextSibling = currentNode.parentElement.nextSibling;
        if (nextTextSibling && nextTextSibling.nodeType === Node.TEXT_NODE) {
          range.setStart(nextTextSibling, 0);
        } else {
          range.setStartAfter(currentNode.parentElement);
        }
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }

      const range = document.createRange();
      range.setStart(currentNode, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= length;
    currentNode = walker.nextNode() as Text | null;
  }

  // Fallback: place caret at the end
  const range = document.createRange();
  range.selectNodeContents(container);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

const serializeContent = (container: HTMLElement): string => {
  const parts: string[] = [];

  container.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent ?? '');
      return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName === 'A') {
        const anchor = el as HTMLAnchorElement;
        const label = anchor.textContent ?? '';
        const href = anchor.getAttribute('href') ?? '';
        parts.push(`[${label}](${href})`);
        return;
      }

      if (el.dataset.mention === 'true') {
        parts.push(el.textContent ?? '');
        return;
      }

      if (el.childNodes.length > 0) {
        parts.push(serializeContent(el));
      }
    }
  });

  return parts.join('');
};

const getMentionAnchorPosition = (
  container: HTMLElement,
): { top: number; left: number } => {
  const selection = window.getSelection();
  const range = selection?.rangeCount
    ? selection.getRangeAt(0).cloneRange()
    : null;
  const rect = range ? range.getBoundingClientRect() : null;
  const containerRect = container.getBoundingClientRect();

  if (rect) {
    return {
      top: rect.bottom - containerRect.top + 6,
      left: rect.left - containerRect.left,
    };
  }

  return { top: 12, left: 12 };
};

export const MDTextarea = forwardRef<MDTextareaHandle, Props>(
  ({ value, onChange, placeholder = 'Tell us something...' }: Props, ref) => {
    const editableValue = useMemo(() => parseEditable(value), [value]);
    const caretOffsetRef = useRef<number | null>(null);
    const caretSeqRef = useRef(0);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const activeLinkRef = useRef<typeof activeLink>(null);
    const hasFocusedOnceRef = useRef(false);
    const [activeLink, setActiveLink] = useState<{
      label: string;
      href: string;
      start: number;
      end: number;
      top: number;
      left: number;
      reason: 'hover' | 'cursor' | 'click';
    } | null>(null);
    const [isPointerInTooltip, setIsPointerInTooltip] = useState(false);
    const [mentionPrompt, setMentionPrompt] = useState<{
      start: number;
      end: number;
      anchorTop: number;
      anchorLeft: number;
    } | null>(null);
    const [linkModal, setLinkModal] = useState<{
      mode: 'add' | 'edit';
      start: number;
      end: number;
      label: string;
      href: string;
    } | null>(null);
    const pendingFocusRef = useRef(false);

    const storeCaretOffset = useCallback((target: HTMLDivElement) => {
      caretOffsetRef.current = getCaretOffset(target);
      caretSeqRef.current += 1;
    }, []);

    const handleInput = useCallback(
      (event: React.FormEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        storeCaretOffset(target);
        const serialized = serializeContent(target);

        onChange(serialized);
        const caretRendered = getCaretOffset(target) ?? serialized.length;
        if (containerRef.current) {
          const caretOffset = renderedOffsetToMarkdownOffset(
            editableValue,
            caretRendered,
          );
          const triggerStart = getChannelMentionTriggerPosition(
            serialized,
            caretOffset,
          );
          if (triggerStart >= 0) {
            const anchor = getMentionAnchorPosition(containerRef.current);
            const triggerEnd = caretOffset;
            setMentionPrompt({
              start: triggerStart,
              end: triggerEnd,
              anchorTop: anchor.top,
              anchorLeft: anchor.left,
            });
          } else {
            setMentionPrompt(null);
          }
        }
      },
      [editableValue, onChange, storeCaretOffset],
    );

    const handleSelectionChange = useCallback(() => {
      if (!containerRef.current) return;
      storeCaretOffset(containerRef.current);
    }, [storeCaretOffset]);

    const showLinkDetails = useCallback(
      (anchor: HTMLAnchorElement, reason: 'click') => {
        if (!containerRef.current) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const rect = anchor.getBoundingClientRect();
        const label = anchor.textContent ?? '';
        const href = anchor.getAttribute('href') ?? '';
        const start = Number(anchor.dataset.mdStart ?? '-1');
        const end = Number(anchor.dataset.mdEnd ?? '-1');

        setActiveLink({
          label,
          href,
          start,
          end,
          top: rect.bottom - containerRect.top + 8,
          left: rect.left - containerRect.left,
          reason,
        });
      },
      [],
    );

    const maybeShowLinkFromSelection = useCallback(() => {
      const container = containerRef.current;
      const caretOffset = container ? getCaretOffset(container) : null;
      if (container && caretOffset !== null) {
        const caretMd = renderedOffsetToMarkdownOffset(
          editableValue,
          caretOffset,
        );
        const triggerStart = getChannelMentionTriggerPosition(value, caretMd);
        if (triggerStart >= 0) {
          const anchorPos = getMentionAnchorPosition(container);
          setMentionPrompt({
            start: triggerStart,
            end: caretMd,
            anchorTop: anchorPos.top,
            anchorLeft: anchorPos.left,
          });
        } else {
          setMentionPrompt(null);
        }
      }
    }, [editableValue, value]);

    const handleFocus = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;
      const textLength = container.textContent?.length ?? 0;
      if (pendingFocusRef.current && caretOffsetRef.current !== null) {
        const target = Math.min(caretOffsetRef.current, textLength);
        hasFocusedOnceRef.current = true;
        requestAnimationFrame(() => {
          setCaretOffset(container, target);
          maybeShowLinkFromSelection();
        });
        return;
      }
      const offset = hasFocusedOnceRef.current
        ? getCaretOffset(container) ?? textLength
        : textLength;
      caretOffsetRef.current = offset;
      requestAnimationFrame(() => {
        setCaretOffset(container, offset);
        maybeShowLinkFromSelection();
      });
      hasFocusedOnceRef.current = true;
    }, [maybeShowLinkFromSelection]);

    const handleRemoveLink = useCallback(() => {
      const link = activeLinkRef.current ?? activeLink;
      if (!link) return;
      const newValue = value.slice(0, link.start) + value.slice(link.end);
      caretOffsetRef.current = link.start;
      setActiveLink(null);
      setIsPointerInTooltip(false);
      onChange(newValue);
    }, [activeLink, onChange, value]);

    const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
      const rt = e.relatedTarget as HTMLElement | null;
      if (rt?.dataset?.linkTooltip === 'true') return;
      setActiveLink(null);
      setIsPointerInTooltip(false);
      setMentionPrompt(null);
    }, []);

    const handleEditLink = useCallback(() => {
      const link = activeLinkRef.current ?? activeLink;
      if (!link) return;
      setActiveLink(null);
      setIsPointerInTooltip(false);
      setLinkModal({
        mode: 'edit',
        start: link.start,
        end: link.end,
        label: link.label,
        href: link.href,
      });
    }, [activeLink]);

    const handleMentionKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!mentionPrompt && event.key === '@' && containerRef.current) {
          const selection = window.getSelection();
          const range =
            selection?.rangeCount && selection.getRangeAt(0).cloneRange();
          const rect = range ? range.getBoundingClientRect() : null;
          const containerRect =
            containerRef.current.getBoundingClientRect() ?? null;
          const caretOffset =
            getCaretOffset(containerRef.current) ??
            containerRef.current.textContent?.length ??
            0;
          if (rect && containerRect) {
            setMentionPrompt({
              start: caretOffset,
              end: caretOffset,
              anchorTop: rect.bottom - containerRect.top + 6,
              anchorLeft: rect.left - containerRect.left,
            });
          } else {
            setMentionPrompt({
              start: caretOffset,
              end: caretOffset,
              anchorTop: 12,
              anchorLeft: 12,
            });
          }
          return;
        }
        if (!mentionPrompt) return;
        const insertMention = () => {
          const triggerStart = mentionPrompt.start;
          const triggerEnd = mentionPrompt.end ?? mentionPrompt.start + 1;
          const insertion = `${COMMUNITY_MENTION} `;
          const newValue =
            value.slice(0, triggerStart) + insertion + value.slice(triggerEnd);
          caretOffsetRef.current = triggerStart + insertion.length;
          setMentionPrompt(null);
          onChange(newValue);
        };
        if (event.key === 'Enter') {
          event.preventDefault();
          insertMention();
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setMentionPrompt(null);
        }
      },
      [mentionPrompt, onChange, value],
    );

    const handleMentionClick = useCallback(() => {
      if (!mentionPrompt) return;
      const insertion = `${COMMUNITY_MENTION} `;
      const triggerEnd = mentionPrompt.end ?? mentionPrompt.start + 1;
      const newValue =
        value.slice(0, mentionPrompt.start) +
        insertion +
        value.slice(triggerEnd);
      caretOffsetRef.current = mentionPrompt.start + insertion.length;
      setMentionPrompt(null);
      onChange(newValue);
    }, [mentionPrompt, onChange, value]);

    useLayoutEffect(() => {
      activeLinkRef.current = activeLink;
    }, [activeLink]);

    const handleLinkModalSave = useCallback(
      ({ name, href }: { name: string; href: string }) => {
        if (!linkModal) return;
        const start = Math.max(0, Math.min(linkModal.start, value.length));
        const end = Math.max(start, Math.min(linkModal.end, value.length));
        const res = insertLinkAtSelection(
          value,
          { start, end },
          { name, href },
        );
        if (!res.allowed) {
          setLinkModal(null);
          return;
        }
        const newSegments = parseEditable(res.value);
        const renderedCaret = markdownOffsetToRenderedOffset(
          newSegments,
          res.caretOffset,
        );
        caretOffsetRef.current = renderedCaret;
        pendingFocusRef.current = true;
        setLinkModal(null);
        setActiveLink(null);
        onChange(res.value);
        requestAnimationFrame(() => {
          const target = containerRef.current;
          if (!target || caretOffsetRef.current === null) return;
          target.focus();
          setCaretOffset(
            target,
            Math.min(caretOffsetRef.current, target.textContent?.length ?? 0),
          );
          pendingFocusRef.current = false;
        });
        setTimeout(() => {
          const target = containerRef.current;
          if (!target || caretOffsetRef.current === null) return;
          target.focus();
          setCaretOffset(
            target,
            Math.min(caretOffsetRef.current, target.textContent?.length ?? 0),
          );
          pendingFocusRef.current = false;
        }, 0);
      },
      [linkModal, onChange, value],
    );

    const openAddLink = useCallback(() => {
      if (!containerRef.current) return;
      const selectionInfo = getSelectionOffsets(containerRef.current);
      const renderedStart = selectionInfo?.start ?? selectionInfo?.end ?? 0;
      const renderedEnd =
        selectionInfo?.end ??
        getCaretOffset(containerRef.current) ??
        renderedStart;

      const mdStart = renderedOffsetToMarkdownOffset(
        editableValue,
        renderedStart,
      );
      const mdEnd = renderedOffsetToMarkdownOffset(editableValue, renderedEnd);
      const normalizedStart = Math.min(mdStart, mdEnd);
      const normalizedEnd = Math.max(mdStart, mdEnd);

      const seg = findSegmentAt(editableValue, normalizedStart);
      if (!seg || seg.type !== 'text' || normalizedEnd > seg.end) {
        return;
      }

      const label = value.slice(normalizedStart, normalizedEnd);
      setLinkModal({
        mode: 'add',
        start: normalizedStart,
        end: normalizedEnd,
        label,
        href: '',
      });
    }, [editableValue, value]);

    useImperativeHandle(ref, () => ({ openAddLink }), [openAddLink]);

    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const shouldRestoreCaret =
        document.activeElement === container && caretOffsetRef.current !== null;
      const expectedSeq = caretSeqRef.current;

      const fragment = document.createDocumentFragment();
      editableValue.forEach((v) => {
        if (v.type === 'text') {
          fragment.appendChild(document.createTextNode(v.value));
        }
        if (v.type === 'mention') {
          const mention = document.createElement('span');
          mention.textContent = v.value;
          mention.dataset.mention = 'true';
          mention.className = styles.mention;
          fragment.appendChild(mention);
        }
        if (v.type === 'link') {
          const anchor = document.createElement('a');
          anchor.href = sanitizeUrl(v.value.href) || '#';
          anchor.target = '_blank';
          anchor.rel = 'noopener noreferrer';
          anchor.textContent = v.value.name;
          anchor.className = styles.link;
          anchor.dataset.mdStart = String(v.start);
          anchor.dataset.mdEnd = String(v.end);
          anchor.onclick = (e) => {
            e.preventDefault();
            showLinkDetails(anchor, 'click');
          };
          anchor.onmousedown = (e) => e.preventDefault();
          anchor.onmouseup = (e) => e.preventDefault();
          fragment.appendChild(anchor);
        }
      });

      // Full sync: replace user-mutated DOM with state-driven content
      container.replaceChildren(fragment);

      const totalLength = container.textContent?.length ?? 0;
      const targetOffset = Math.min(caretOffsetRef.current ?? 0, totalLength);

      if (pendingFocusRef.current && caretOffsetRef.current !== null) {
        container.focus();
        setCaretOffset(container, targetOffset);
        pendingFocusRef.current = false;
        return;
      }

      if (!shouldRestoreCaret) return;
      if (expectedSeq !== caretSeqRef.current) return;

      setCaretOffset(container, targetOffset);
    }, [editableValue]);

    return (
      <div className={styles.container} onBlur={handleBlur}>
        <div
          ref={containerRef}
          className={styles.editor}
          contentEditable
          role="textbox"
          aria-multiline="true"
          suppressContentEditableWarning
          onInput={handleInput}
          onFocus={handleFocus}
          onKeyDown={(e) => {
            handleMentionKeyDown(e);
          }}
          onKeyUp={(e) => {
            handleSelectionChange();
            maybeShowLinkFromSelection();
          }}
          onMouseUp={(e) => {
            handleSelectionChange();
            maybeShowLinkFromSelection();
          }}
        />
        {placeholder && !value && (
          <div className={styles.placeholder}>{placeholder}</div>
        )}
        {mentionPrompt && (
          <MDMentionMenu
            top={mentionPrompt.anchorTop}
            left={mentionPrompt.anchorLeft}
            onSelect={handleMentionClick}
          />
        )}
        {activeLink && (
          <MDLinkTooltip
            top={activeLink.top}
            left={activeLink.left}
            label={activeLink.label}
            href={activeLink.href}
            onClose={() => setActiveLink(null)}
            onEdit={handleEditLink}
            onRemove={handleRemoveLink}
            onMouseEnter={() => setIsPointerInTooltip(true)}
            onMouseLeave={() => setIsPointerInTooltip(false)}
          />
        )}
        {linkModal && (
          <MDLinkModal
            open
            mode={linkModal.mode}
            initialLabel={linkModal.label}
            initialHref={linkModal.href}
            onClose={() => setLinkModal(null)}
            onSave={handleLinkModalSave}
          />
        )}
      </div>
    );
  },
);

MDTextarea.displayName = 'MDTextarea';
